import { useEffect } from "react";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  noIndex?: boolean;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const SITE_NAME = "Bull on the Loose Food Truck";
const DEFAULT_IMAGE = "/images/TruckImage.webp";

function upsertMeta(
  selector: string,
  selectorValue: string,
  content: string
): void {
  let tag = document.head.querySelector(
    `meta[${selector}="${selectorValue}"]`
  ) as HTMLMetaElement | null;

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(selector, selectorValue);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function upsertCanonical(href: string): void {
  let link = document.head.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

function upsertStructuredData(value: string): void {
  const scriptId = "seo-structured-data";
  let script = document.head.querySelector(
    `script#${scriptId}`
  ) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = scriptId;
    document.head.appendChild(script);
  }

  script.textContent = value;
}

function getAbsoluteUrl(path: string): string {
  const configuredSiteUrl = import.meta.env.VITE_SITE_URL;
  const base =
    typeof configuredSiteUrl === "string" && configuredSiteUrl.trim().length > 0
      ? configuredSiteUrl
      : window.location.origin;

  return new URL(path, base).toString();
}

export default function Seo({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  type = "website",
  noIndex = false,
  structuredData,
}: SeoProps) {
  const structuredDataString = structuredData
    ? JSON.stringify(structuredData)
    : null;

  useEffect(() => {
    const fullTitle = `${title} | ${SITE_NAME}`;
    const canonicalUrl = getAbsoluteUrl(path);
    const imageUrl = getAbsoluteUrl(image);

    document.title = fullTitle;
    document.documentElement.lang = "en";

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noIndex ? "noindex, nofollow" : "index, follow");

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:site_name", SITE_NAME);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);

    upsertCanonical(canonicalUrl);

    if (structuredDataString) {
      upsertStructuredData(structuredDataString);
    }
  }, [description, image, noIndex, path, structuredDataString, title, type]);

  return null;
}
